/**
 * ConfigHistory Component
 *
 * Displays the history of platform configuration changes.
 */

import { useConfigHistory } from '../hooks';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

export const ConfigHistory = () => {
  const { history, isLoading } = useConfigHistory();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
            <div className="h-16 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p>No configuration changes recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((entry, index) => (
        <div
          key={entry.id}
          className="relative pl-6 pb-4 border-l-2 border-gray-200 last:pb-0"
        >
          {/* Timeline dot */}
          <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-white border-2 border-primary-500" />

          {/* Entry content */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {entry.changedByName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(entry.changedAt)} ({formatRelativeTime(entry.changedAt)})
                </p>
              </div>
              {index === 0 && (
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  Current
                </span>
              )}
            </div>

            <p className="text-sm text-gray-700 mb-3">{entry.reason}</p>

            {/* Changes summary */}
            <div className="text-xs bg-white rounded-lg p-3 border border-gray-200">
              <p className="font-medium text-gray-700 mb-2">Changes:</p>
              {entry.newConfig.fees?.platformCommission && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-gray-500">Platform Commission (10%):</span>
                  <span className="text-red-500 line-through">
                    {entry.previousConfig.fees?.platformCommission?.value || 0}%
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-green-600 font-medium">
                    {entry.newConfig.fees.platformCommission.value}%
                  </span>
                </div>
              )}
              {entry.newConfig.payouts && (
                <>
                  {entry.newConfig.payouts.minimumThreshold !==
                    entry.previousConfig.payouts?.minimumThreshold && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-500">Min Payout:</span>
                      <span className="text-red-500 line-through">
                        ${entry.previousConfig.payouts?.minimumThreshold || 0}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-600 font-medium">
                        ${entry.newConfig.payouts.minimumThreshold}
                      </span>
                    </div>
                  )}
                  {entry.newConfig.payouts.wireTransferFee !==
                    entry.previousConfig.payouts?.wireTransferFee && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Wire Fee:</span>
                      <span className="text-red-500 line-through">
                        ${entry.previousConfig.payouts?.wireTransferFee || 0}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-600 font-medium">
                        ${entry.newConfig.payouts.wireTransferFee}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ConfigHistory;
